import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Search, Users, FileText, X, Mail, UserPlus, Download } from "lucide-react";

export default function CompaniesTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", gstin: "", address: "", contact_email: "", contact_phone: "", billing_email: "" });
  const [selectedCo, setSelectedCo] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [newEmpEmail, setNewEmpEmail] = useState("");
  const [statementMonth, setStatementMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [statement, setStatement] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/companies${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setItems(r.data.companies || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load companies"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const submitCreate = async () => {
    if (!form.name.trim()) { toast.error("Company name required"); return; }
    try {
      await api.post("/admin/companies", form);
      toast.success("Company added");
      setShowCreate(false);
      setForm({ name: "", gstin: "", address: "", contact_email: "", contact_phone: "", billing_email: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create company"); }
  };

  const openCompany = async (co) => {
    setSelectedCo(co);
    setStatement(null);
    try {
      const r = await api.get(`/admin/companies/${co.id}/employees`);
      setEmployees(r.data.employees || []);
    } catch (e) { toast.error("Failed to load employees"); }
  };

  const linkEmp = async () => {
    if (!newEmpEmail.trim()) return;
    try {
      await api.post(`/admin/companies/${selectedCo.id}/employees`, { email: newEmpEmail.trim() });
      toast.success("Employee linked");
      setNewEmpEmail("");
      openCompany(selectedCo);
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
  };

  const unlinkEmp = async (uid) => {
    if (!window.confirm("Unlink this employee from the company?")) return;
    try {
      await api.delete(`/admin/companies/${selectedCo.id}/employees/${uid}`);
      toast.success("Unlinked");
      openCompany(selectedCo);
    } catch (e) { toast.error(e.response?.data?.detail || "Unlink failed"); }
  };

  const loadStatement = async () => {
    try {
      const r = await api.get(`/admin/companies/${selectedCo.id}/statement?month=${statementMonth}`);
      setStatement(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load statement"); }
  };

  const downloadStatementCsv = () => {
    if (!statement) return;
    const rows = [
      ["Booking ID", "Created", "Listing", "Check-in", "Status", "Amount (₹)", "GST (₹)", "Customer", "Email"],
      ...((statement.bookings || []).map((b) => [
        b.id, (b.created_at || "").slice(0, 10), b.listing_title || "", b.check_in || "",
        b.booking_status || "", b.amount || 0, b.gst_amount || 0, b.customer_name || "", b.customer_email || "",
      ])),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${statement.company.name.replace(/\W+/g, "_")}_${statementMonth}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
  };

  return (
    <div data-testid="admin-companies-tab">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-[#1A231C] font-['Outfit']">Corporate Accounts (B2B)</h2>
          <p className="text-sm text-[#8A998E]">{total} companies · employees auto-tag bookings with company GSTIN for GST invoicing</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A998E]" />
            <Input className="pl-9 h-9 bg-white border-[#E5E3DB] rounded-full text-sm w-64" placeholder="Search by name / GSTIN / email"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="companies-search" />
          </div>
          <Button onClick={() => setShowCreate(true)} className="rounded-full bg-[#2C5F2D] hover:bg-[#234B24] text-white" data-testid="add-company-btn">
            <Plus className="w-4 h-4 mr-1" /> New company
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-[#8A998E]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-[#F3F2EE] rounded-xl">
          <Building2 className="w-10 h-10 mx-auto text-[#8A998E] mb-3" />
          <p className="text-sm text-[#445049] font-medium">No corporate accounts yet</p>
          <p className="text-xs text-[#8A998E] mt-1">Add a company to enable GST invoicing &amp; monthly statements</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((c) => (
            <button key={c.id} onClick={() => openCompany(c)}
              className="text-left bg-white border border-[#E5E3DB] rounded-xl p-4 hover:border-[#2C5F2D] hover:shadow transition"
              data-testid={`company-card-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#2C5F2D]/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-[#2C5F2D]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <strong className="text-sm truncate">{c.name}</strong>
                    <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
                  </div>
                  {c.gstin && <p className="text-[11px] font-mono text-[#445049] mt-0.5">GSTIN: {c.gstin}</p>}
                  <p className="text-[11px] text-[#8A998E] mt-1">
                    <Users className="w-3 h-3 inline mr-1" />{c.employee_count || 0} employees
                    {c.contact_email && (<>  <Mail className="w-3 h-3 inline ml-2 mr-1" />{c.contact_email}</>)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-company-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">New corporate account</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs font-semibold uppercase text-[#5C6B60]">Company name *</Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="co-name-input" /></div>
            <div><Label className="text-xs font-semibold uppercase text-[#5C6B60]">GSTIN <span className="text-[#8A998E] normal-case">(15-char, optional)</span></Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1 font-mono uppercase" value={form.gstin} onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="29ABCDE1234F2Z5" maxLength={15} data-testid="co-gstin-input" /></div>
            <div><Label className="text-xs font-semibold uppercase text-[#5C6B60]">Address</Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="co-address-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs font-semibold uppercase text-[#5C6B60]">Contact email</Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1" type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} data-testid="co-email-input" /></div>
              <div><Label className="text-xs font-semibold uppercase text-[#5C6B60]">Phone</Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1" value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} data-testid="co-phone-input" /></div>
            </div>
            <div><Label className="text-xs font-semibold uppercase text-[#5C6B60]">Billing email <span className="text-[#8A998E] normal-case">(defaults to contact)</span></Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1" type="email" value={form.billing_email} onChange={(e) => setForm((f) => ({ ...f, billing_email: e.target.value }))} data-testid="co-billing-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="rounded-full bg-[#2C5F2D] hover:bg-[#234B24] text-white" onClick={submitCreate} data-testid="co-submit-btn">Create company</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail panel */}
      {selectedCo && (
        <Dialog open={!!selectedCo} onOpenChange={() => setSelectedCo(null)}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="company-detail-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit']">{selectedCo.name}</DialogTitle></DialogHeader>
            <div className="text-xs text-[#8A998E] -mt-3 mb-4">
              {selectedCo.gstin && <span className="font-mono mr-3">{selectedCo.gstin}</span>}
              {selectedCo.contact_email && <span className="mr-3">{selectedCo.contact_email}</span>}
              {selectedCo.address && <span>{selectedCo.address}</span>}
            </div>

            {/* Employees */}
            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Employees ({employees.length})</h4>
            <div className="flex gap-2 mb-3">
              <Input className="bg-white border-[#E5E3DB] rounded-full text-sm h-9 flex-1" placeholder="Employee email (must be a registered customer)" value={newEmpEmail} onChange={(e) => setNewEmpEmail(e.target.value)} data-testid="link-emp-email" />
              <Button size="sm" className="rounded-full bg-[#2C5F2D] hover:bg-[#234B24] text-white" onClick={linkEmp} data-testid="link-emp-btn"><UserPlus className="w-3.5 h-3.5 mr-1" /> Link</Button>
            </div>
            <div className="space-y-1 mb-6 max-h-48 overflow-y-auto">
              {employees.length === 0 ? (
                <p className="text-xs text-[#8A998E] italic">No employees yet — link a registered customer above.</p>
              ) : employees.map((e) => (
                <div key={e.id} className="flex items-center justify-between bg-[#F3F2EE] rounded-lg px-3 py-2" data-testid={`emp-row-${e.id}`}>
                  <span className="text-xs"><strong>{e.name}</strong> · {e.email}</span>
                  <Button size="sm" variant="ghost" onClick={() => unlinkEmp(e.id)} data-testid={`unlink-emp-${e.id}`}><X className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>

            {/* Monthly statement */}
            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Monthly statement</h4>
            <div className="flex gap-2 mb-3 items-end">
              <div>
                <Label className="text-xs">Month</Label>
                <Input type="month" className="bg-white border-[#E5E3DB] rounded-xl text-sm h-9 mt-1" value={statementMonth} onChange={(e) => setStatementMonth(e.target.value)} data-testid="statement-month-input" />
              </div>
              <Button size="sm" className="rounded-full bg-[#2C5F2D] hover:bg-[#234B24] text-white" onClick={loadStatement} data-testid="load-statement-btn">Load</Button>
              {statement && (
                <Button size="sm" variant="outline" className="rounded-full" onClick={downloadStatementCsv} data-testid="dl-statement-csv"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
              )}
            </div>
            {statement && (
              <div data-testid="statement-result">
                <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                  <div className="bg-[#F3F2EE] rounded p-2"><div className="text-[#8A998E]">Total bookings</div><strong className="text-base">{statement.summary.total_bookings}</strong></div>
                  <div className="bg-[#F3F2EE] rounded p-2"><div className="text-[#8A998E]">Total amount</div><strong className="text-base">₹{statement.summary.total_amount.toLocaleString("en-IN")}</strong></div>
                  <div className="bg-[#F3F2EE] rounded p-2"><div className="text-[#8A998E]">Total GST</div><strong className="text-base">₹{statement.summary.total_gst.toLocaleString("en-IN")}</strong></div>
                </div>
                <div className="overflow-x-auto"><table className="text-xs w-full"><thead className="bg-[#F3F2EE]"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Listing</th><th className="text-left p-2">Customer</th><th className="text-right p-2">Amount</th><th className="text-left p-2">Status</th></tr></thead><tbody>
                  {(statement.bookings || []).map((b) => (
                    <tr key={b.id} className="border-b border-[#E5E3DB]">
                      <td className="p-2">{(b.created_at || "").slice(0, 10)}</td>
                      <td className="p-2">{b.listing_title || ""}</td>
                      <td className="p-2">{b.customer_name || ""}</td>
                      <td className="p-2 text-right">₹{(b.amount || 0).toLocaleString("en-IN")}</td>
                      <td className="p-2"><Badge variant="outline" className="text-[10px]">{b.booking_status}</Badge></td>
                    </tr>
                  ))}
                </tbody></table></div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
